// Shared Supabase connection helpers.
//
// No production credentials live in this file. The URL and publishable key come
// from the environment-aware runtime configuration:
//   - AWS production: injected by the server from AWS Secrets Manager.
//   - Lovable: the Lovable-managed .env (Lovable Cloud).
// See ./runtime-config.ts for the exact priority order.
//
// Server-only secrets (service-role key, Google client secret,
// APP_USER_CONNECTION_KEY_SECRET) must never be referenced here.

import {
  readPublicSupabaseConfig,
  requirePublicSupabaseConfig,
  type PublicSupabaseConfig,
} from "./runtime-config";

/** Current Supabase configuration, or null when unconfigured. */
export function supabaseConfig(): PublicSupabaseConfig | null {
  return readPublicSupabaseConfig();
}

/** Base URL of the active Supabase instance. */
export function supabaseUrl(): string {
  return requirePublicSupabaseConfig().url;
}

/** Publishable (anon) key of the active Supabase instance. */
export function supabasePublishableKey(): string {
  return requirePublicSupabaseConfig().publishableKey;
}

/**
 * Storage key for the browser session. Scoped per environment so a Lovable Cloud
 * session and a production session can never be mistaken for one another.
 */
export function supabaseStorageKey(source: PublicSupabaseConfig["source"]): string {
  return source === "aws-production" ? "sb-leadershipedge-auth-token" : "sb-lovable-cloud-auth-token";
}

/** New-format Supabase API keys are opaque strings, not bearer JWTs. */
export function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith("sb_publishable_") || value.startsWith("sb_secret_");
}

/**
 * supabase-js sends `Authorization: Bearer <apikey>` by default, which the
 * self-hosted gateway rejects for opaque keys. Strip it and send `apikey`.
 */
export function selfHostedFetch(apiKey: string, bearerToken?: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined,
    );
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }
    if (isNewSupabaseApiKey(apiKey) && headers.get("Authorization") === `Bearer ${apiKey}`) {
      headers.delete("Authorization");
    }
    if (bearerToken) headers.set("Authorization", `Bearer ${bearerToken}`);
    headers.set("apikey", apiKey);
    return fetch(input, { ...init, headers });
  };
}
